"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import CultureSection from "./culture-section";
import JobListSection from "./job-list-section";
import { useEffect, useState, useRef } from "react";
import type {
  SectionContent,
  SectionData,
  SectionStyle,
} from "@/lib/types/cms-types";
import { CareerPageSkeleton } from "./skeletons";
import { useEnv } from "@/context/envContext";
import LocomotiveScroll from 'locomotive-scroll';
import LifeAtDana from "./life-at-dana";
import { CareerNavbarAuthButton } from "./career-navbar-auth-button";

type CmsData = {
  [key: string]: SectionData;
};

type CmsResponseItem = {
  sectionKey: string;
  content: SectionContent;
  style: SectionStyle;
};

export function CareerPage() {
  const [cmsData, setCmsData] = useState<CmsData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const env = useEnv();
  const scrollRef = useRef<HTMLDivElement>(null);
  const locomotiveRef = useRef<LocomotiveScroll | null>(null);

  useEffect(() => {
    const controlNavbar = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) { // scrolling down & not at the top
        setIsNavbarVisible(false);
      } else { // scrolling up or at the top
        setIsNavbarVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlNavbar);
    
    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, [lastScrollY]);

  useEffect(() => {
    const fetchCmsData = async () => {
      try {
        const response = await fetch(
          `${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/career/`
        );
        const result = await response.json();
        if (result.status === 200) {
          const transformedData = result.data.reduce(
            (acc: CmsData, item: CmsResponseItem) => ({
              ...acc,
              [item.sectionKey]: {
                content: item.content,
                style: item.style,
              },
            }),
            {}
          );
          setCmsData(transformedData);
        }
      } catch (error) {
        console.error("Error fetching CMS data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (env.NEXT_PUBLIC_EXTERNAL_API_URL) {
      fetchCmsData();
    }
  }, [env.NEXT_PUBLIC_EXTERNAL_API_URL]);

  useEffect(() => {
    const handleHashScroll = () => {
      const hash = window.location.hash;
      if (!hash) return;
      const target = hash.startsWith('#') ? hash : `#${hash}`;
      // Use locomotive when available; fallback to native
      if (locomotiveRef.current) {
        locomotiveRef.current.scrollTo(target, { offset: 0, duration: 800 });
      } else {
        const el = document.querySelector(target);
        if (el) (el as HTMLElement).scrollIntoView({ behavior: 'smooth' });
      }
    };

    const shouldUseLocomotive =
      scrollRef.current && window.matchMedia('(min-width: 1024px)').matches;

    if (shouldUseLocomotive) {
      const scroll = new LocomotiveScroll({
        el: scrollRef.current,
        smooth: true,
        lerp: 0.1,
      } as any);

      locomotiveRef.current = scroll;
    }

    // Initial hash navigation after locomotive ready
    requestAnimationFrame(() => {
      handleHashScroll();
    });

    window.addEventListener('hashchange', handleHashScroll);

    return () => {
      window.removeEventListener('hashchange', handleHashScroll);
      locomotiveRef.current?.destroy();
      locomotiveRef.current = null;
    };
  }, []);

  const scrollToId = (id: string) => {
    const selector = id.startsWith('#') ? id : `#${id}`;
    if (locomotiveRef.current) {
      locomotiveRef.current.scrollTo(selector, { offset: 0, duration: 800 });
    } else {
      const target = document.querySelector(selector);
      if (target) (target as HTMLElement).scrollIntoView({ behavior: 'smooth' });
    }
    // Update URL hash without immediate native jump
    history.replaceState(null, '', selector);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  if (isLoading) {
    return <CareerPageSkeleton />;
  }

  return (
    <div
      ref={scrollRef}
      data-scroll-container
      className="relative w-full max-w-[100vw] overflow-x-hidden"
    >
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-white shadow-sm transition-transform duration-300 ${
        isNavbarVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center">
              <Image
                src="/dana-blue.webp"
                alt="DANA"
                width={100}
                height={40}
                className="object-contain"
                priority
              />
            </Link>

            {/* Mobile menu button */}
            <div className="flex items-center gap-3">
              <CareerNavbarAuthButton className="hidden sm:inline-flex" />
              <button 
                className="lg:hidden flex items-center p-2"
                onClick={toggleMobileMenu}
                aria-label="Toggle mobile menu"
              >
              <svg 
                className="w-6 h-6" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
                />
              </svg>
            </button>
            </div>

            {/* Navigation links - desktop */}
            {/* <div className="hidden lg:flex items-center space-x-8">
              <a 
                href="#culture" 
                className="text-gray-700 hover:text-blue-600 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId('culture');
                }}
              >
                Our Culture
              </a>
              <a 
                href="#job-list" 
                className="text-gray-700 hover:text-blue-600 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId('job-list');
                }}
              >
                Open Positions
              </a>
            </div> */}
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden bg-white py-4 border-t">
              <div className="flex flex-col space-y-4 px-4">
                <CareerNavbarAuthButton
                  className="block py-2 sm:hidden"
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
                <a 
                  href="#culture" 
                  className="text-gray-700 hover:text-blue-600 transition-colors py-2"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMobileMenuOpen(false);
                    scrollToId('culture');
                  }}
                >
                  Our Culture
                </a>
                <a 
                  href="#job-list" 
                  className="text-gray-700 hover:text-blue-600 transition-colors py-2"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMobileMenuOpen(false);
                    scrollToId('job-list');
                  }}
                >
                  Open Positions
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        data-scroll-section
        className="relative w-full min-h-screen overflow-hidden p-4 sm:p-8 pt-20 flex items-center"
        style={{ backgroundColor: "#f7f9fc" }}
      >
        {/* Background Image */}
        <div className="absolute right-0 top-0 h-screen w-full lg:w-4/5 overflow-hidden">
          <div className="h-full w-full lg:rounded-l-[48px] overflow-hidden">
            <Image
              src="/banner-1.webp"
              alt="Background pattern"
              width={1920}
              height={1080}
              className="object-cover w-full h-full lg:rounded-l-[48px]"
              sizes="(max-width: 1024px) 100vw, 75vw"
              style={{
                width: '100%',
                height: '100%',
              }}
              loading="lazy"
            />
          </div>
        </div>

        {/* Content Card */}
        <div className="relative container mx-auto">
          <div className="relative z-10 w-full">
            <div className="w-full max-w-[600px] bg-white/95 lg:bg-white rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-xl">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <h1
                  className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight"
                  style={{
                    color: cmsData.hero?.style?.textColor || "#1f2937",
                    fontWeight: cmsData.hero?.style?.fontWeight || "bold",
                    backgroundColor: cmsData.hero?.style?.backgroundColor || "#ffffff",
                  }}
                >
                  {cmsData.hero?.content?.title || "Shape the Future with Us..."}
                </h1>
                <p
                  className="text-base sm:text-lg md:text-xl"
                  style={{
                    color: cmsData.hero?.style?.textColor || "#4b5563",
                  }}
                >
                  {cmsData.hero?.content?.description ||
                    "We are not just a digital wallet..."}
                </p>
                <div className="pt-2 sm:pt-4 flex flex-wrap gap-3 sm:gap-4">
                  <Link
                    href="#job-list"
                    className="inline-flex items-center gap-1 sm:gap-2 bg-blue-600 text-white px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-full text-sm sm:text-base font-medium hover:bg-blue-700 transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId('job-list');
                    }}
                  >
                    {cmsData.hero?.content?.buttonText || "Apply Here"}
                    <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Link>
                  {/* <Link
                    href="#job-list"
                    className="inline-flex items-center gap-1 sm:gap-2 bg-white text-blue-600 border border-blue-600 px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-full text-sm sm:text-base font-medium hover:bg-blue-50 transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId('job-list');
                    }}
                  >
                    View Open Positions
                    <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Link> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Sections */}
      <CultureSection data-scroll-section cmsData={cmsData} />
      <LifeAtDana
        data-scroll-section
        cmsData={cmsData}
        onScrollToJobList={() => scrollToId("job-list")}
      />
      <JobListSection data-scroll-section cmsData={cmsData} /> 
    </div>
  );
}
